import { z } from 'zod'
import { AgreementCitationAgentInputSchema, AgreementCitationResolvedSchema } from './agreementCitation'
import { ChunkCitationAgentInputSchema, ChunkCitationResolvedSchema } from './chunkCitation'
import {
  DocumentReviewFindingCitationAgentInputSchema,
  DocumentReviewFindingCitationResolvedSchema,
} from './documentReviewFindingCitation'
import { OtherRequestFormCitationAgentInputSchema, OtherRequestFormCitationResolvedSchema } from './otherRequestFormCitation'
import { PolicyCitationAgentInputSchema, PolicyCitationResolvedSchema } from './policyCitation'
import {
  PriorSubmissionsSupplierFormCitationAgentInputSchema,
  PriorSubmissionsSupplierFormCitationResolvedSchema,
} from './priorSubmissionsSupplierFormCitation'
import { QuestionCitationAgentInputSchema, QuestionCitationResolvedSchema } from './questionCitation'
import { TaskPrecedentCitationAgentInputSchema, TaskPrecedentCitationResolvedSchema } from './taskPrecedentCitation'
import { UrlCitationAgentInputSchema, UrlCitationResolvedSchema } from './urlCitation'

// Each citation source-type owns one file holding both the LLM input schema and the resolved schema,
// so a slice author finds everything for their source in one place (divide by slice, not by code-type).
// This barrel only assembles the two cross-slice unions and re-exports the per-slice surface.

export * from './agreementCitation'
export * from './chunkCitation'
export * from './citedFact'
export * from './documentReviewFindingCitation'
export * from './otherRequestFormCitation'
export * from './policyCitation'
export * from './priorSubmissionsSupplierFormCitation'
export * from './questionCitation'
export * from './taskPrecedentCitation'
export * from './urlCitation'

// Model-facing citation union. Must contain only variants with a live resolver. A variant added here has
// to be decided for each capability union below too, or that capability sees a slice it cannot cite.
//
// MUST be a plain `z.union`, not `z.discriminatedUnion`: this schema is sent to OpenAI as a strict
// `response_format` json_schema, and OpenAI rejects `oneOf` (which discriminatedUnion serialises to) —
// only `anyOf` (plain union) is permitted.
export const CitationInputSchema = z.union([
  PolicyCitationAgentInputSchema,
  QuestionCitationAgentInputSchema,
  DocumentReviewFindingCitationAgentInputSchema,
  AgreementCitationAgentInputSchema,
  TaskPrecedentCitationAgentInputSchema,
  ChunkCitationAgentInputSchema,
  PriorSubmissionsSupplierFormCitationAgentInputSchema,
  OtherRequestFormCitationAgentInputSchema,
  UrlCitationAgentInputSchema,
])
export type CitationInput = z.infer<typeof CitationInputSchema>

// The fill-in agent's citation union: every review citation source except the prior-submissions slice
// citation (fill-in never offers that slice, so its resolver isn't wired). Same plain-`z.union` constraint
// as above — this schema is sent to OpenAI as a strict `response_format` json_schema.
export const FillInCitationInputSchema = z.union([
  PolicyCitationAgentInputSchema,
  QuestionCitationAgentInputSchema,
  DocumentReviewFindingCitationAgentInputSchema,
  AgreementCitationAgentInputSchema,
  TaskPrecedentCitationAgentInputSchema,
  ChunkCitationAgentInputSchema,
  OtherRequestFormCitationAgentInputSchema,
  UrlCitationAgentInputSchema,
])
export type FillInCitationInput = z.infer<typeof FillInCitationInputSchema>

// The content generation agent's citation union. Spelled out rather than shared with the fill-in union
// above: the two happen to exclude the same slice today, and a capability's citable sources should be
// readable here without tracing whose list it borrowed.
export const ContentGenerationCitationInputSchema = z.union([
  PolicyCitationAgentInputSchema,
  QuestionCitationAgentInputSchema,
  DocumentReviewFindingCitationAgentInputSchema,
  AgreementCitationAgentInputSchema,
  TaskPrecedentCitationAgentInputSchema,
  ChunkCitationAgentInputSchema,
  OtherRequestFormCitationAgentInputSchema,
  UrlCitationAgentInputSchema,
])
export type ContentGenerationCitationInput = z.infer<typeof ContentGenerationCitationInputSchema>

// Optional stable per-citation `id`, stamped at persist time in `processParagraphs` and read by the FE as
// the feedback `subOutputId`. `ChunkCitationResolvedSchema` carries it on its own base (a nested `scope` union).
const citationIdField = { id: z.string().optional() }

// `…Resolved` = an agent-input citation after its reference is looked up against the source, validated
// (stripped if it fails), and enriched with label + navigation ids. This union is every resolved variant,
// persisted on the agent run and read by the FE renderer + generated types.
export const ApprovalCitationSchema = z.discriminatedUnion('sourceType', [
  QuestionCitationResolvedSchema.extend(citationIdField),
  PolicyCitationResolvedSchema.extend(citationIdField),
  DocumentReviewFindingCitationResolvedSchema.extend(citationIdField),
  ChunkCitationResolvedSchema,
  AgreementCitationResolvedSchema.extend(citationIdField),
  PriorSubmissionsSupplierFormCitationResolvedSchema.extend(citationIdField),
  TaskPrecedentCitationResolvedSchema.extend(citationIdField),
  OtherRequestFormCitationResolvedSchema.extend(citationIdField),
  UrlCitationResolvedSchema.extend(citationIdField),
])
export type ApprovalCitation = z.infer<typeof ApprovalCitationSchema>

// The persisted/rendered `question` citation; the inline renderer reads its `id` for per-citation feedback.
export type RenderedQuestionCitation = Extract<ApprovalCitation, { sourceType: 'question' }>
